import NewLookTvAlertSetupModal from "features/bots/NewLook/NewLookTvAlertSetupModal";
import React, { useState } from "react";
import styled from "styled-components";
import { BasicBotData, BotType, LanguageNameSpaces } from "@shared/types";
import useTypedTranslation from "@shared/common/i18n/useTypedTranslation";
import { useSubUserPermissions } from "@shared/utils/hooks/misc";
import { formatDateRelative } from "@shared/utils/date";
import { getNewLookTokens } from "common/NewLook/tokens.ts";
import { NEW_LOOK_PHONE_GUTTER, phoneMaxWidth } from "common/NewLook/layout.ts";
import { BOT_PREVIEW_TRADES_SECTION_ID } from "testIds.ts";

interface NewLookBotFooterProps {
  botData: BasicBotData;
  /** A bot with nothing traded yet: the only one with a footer bar. */
  isNewBot: boolean;
}

/**
 * The footer bar of a new bot, under a hairline: "Created 1h ago" and, for a
 * TradingView bot, "Set up alert ›" (opens the alert guide, not the drawer;
 * not for read-only users).
 * Bots with history have no footer: their meta line is in the "now" section.
 */
const NewLookBotFooter = ({ botData, isNewBot }: NewLookBotFooterProps) => {
  const { t } = useTypedTranslation(LanguageNameSpaces.BOTS);
  const { isReadOnly } = useSubUserPermissions();
  // Read-only users don't get the bot's alert (its UUID), as in the classic
  // card.
  const isSetUp = botData.bot_type === BotType.TRADING_VIEW && !isReadOnly;
  const [setupOpen, setSetupOpen] = useState(false);
  if (!isNewBot) return null;
  return (
    <Footer>
      <Line
        data-testid={
          botData.bot_type === BotType.SIGNAL
            ? BOT_PREVIEW_TRADES_SECTION_ID
            : undefined
        }
      >
        {t("card.created_ago", {
          time: formatDateRelative(botData.created_at, true, true, true),
        })}
      </Line>
      {isSetUp && (
        <>
          <SetUpLink
            type="button"
            onClick={(event) => {
              // The guide, not the drawer the card opens.
              event.stopPropagation();
              setSetupOpen(true);
            }}
            onKeyDown={(event) => event.stopPropagation()}
          >
            {t("card.set_up_alert")} ›
          </SetUpLink>
          <span onClick={(event) => event.stopPropagation()}>
            <NewLookTvAlertSetupModal
              botData={botData}
              open={setupOpen}
              onClose={() => setSetupOpen(false)}
            />
          </span>
        </>
      )}
    </Footer>
  );
};

export default React.memo(NewLookBotFooter);

const Footer = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  margin-top: auto;
  padding: 14px 24px;
  border-top: 1px solid ${(p) => getNewLookTokens(p.theme).hairline};
  font-size: 13px;
  line-height: 18px;
  color: ${(p) => getNewLookTokens(p.theme).secondary};
  @media (max-width: ${phoneMaxWidth}) {
    padding: 12px ${NEW_LOOK_PHONE_GUTTER}px;
    font-size: 12.5px;
  }
`;
const Line = styled.div`
  flex: 1 1 auto;
  min-width: 0;
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
  font-weight: 400;
  font-variant-numeric: tabular-nums;
  &::first-letter {
    text-transform: uppercase;
  }
`;
const SetUpLink = styled.button`
  flex: none;
  padding: 0;
  border: 0;
  background: none;
  font: inherit;
  font-weight: 500;
  cursor: pointer;
  color: ${(p) => getNewLookTokens(p.theme).accent};
  &:hover {
    text-decoration: underline;
  }
`;
